// @flow

import React from 'react'
import styled from 'styled-components'
import type { ReactComponentStyled } from 'styled-components'
import color from 'michelangelo/dist/Components/styles/color'
import type { Node } from 'react'

// Define what props.theme will look like
const fontColor = {
  default: color.lightDefault,
  info: color.lightInfo,
  warning: color.lightWarning,
  danger: color.lightDanger,
  success: color.lightSuccess,
  white: color.white,
  darkBlue: color.darkerBlue,
  black: color.black
}

const align = { left: 'left', right: 'right', center: 'center', justify: 'justify' }

type CustomTextProps = {
  fontColor: 'default' | 'info' | 'warning' | 'danger' | 'success' | 'white' | 'darkBlue' | 'black'
}

type TextProps = {
  text: string,
  size?: '1' | '2' | '3' | '4' | 'P',
  fontColor: 'default' | 'info' | 'warning' | 'danger' | 'success' | 'white' | 'darkBlue' | 'black',
  align: 'center' | 'left' | 'right' | 'justify',
  children?: Node,
  fontFamily?: string
}

const H1: ReactComponentStyled<{}> = styled.h1`
  text-align: ${(props: TextProps) => align[props.align]};
  font-family: Raleway;
  font-weight: 300;
  font-size: 36px;
  color: ${(props: CustomTextProps) => fontColor[props.fontColor]};
  line-height: 42px;
  text-transform: ${(props) => props.texttransform};
  text-align: ${(props: TextProps) => props.align || 'left'};
  -webkit-margin-before: 0px;
  -webkit-margin-after: 0px;
  -webkit-margin-start: 0px;
  -webkit-margin-end: 0px;
`
const H2: ReactComponentStyled<{}> = styled.h2`
  text-align: ${(props: TextProps) => align[props.align]};
  font-family: Raleway;
  font-weight: 500;
  font-size: 30px;
  color: ${(props: CustomTextProps) => fontColor[props.fontColor]};
  letter-spacing: 0px;
  line-height: 35px;
  -webkit-margin-before: 0px;
  -webkit-margin-after: 0px;
  -webkit-margin-start: 0px;
  -webkit-margin-end: 0px;
`
const H3: ReactComponentStyled<{}> = styled.h3`
  text-align: ${(props: TextProps) => align[props.align]};
  font-family: Raleway;
  font-weight: 500;
  font-size: 24px;
  color: ${(props: CustomTextProps) => fontColor[props.fontColor]};
  letter-spacing: 0px;
  line-height: 28px;
  -webkit-margin-before: 0px;
  -webkit-margin-after: 0px;
  -webkit-margin-start: 0px;
  -webkit-margin-end: 0px;
`
const H4: ReactComponentStyled<{}> = styled.h4`
  text-align: ${(props: TextProps) => align[props.align]};
  font-family: Roboto;
  font-weight: 700;
  font-size: 18px;
  color: ${(props: CustomTextProps) => fontColor[props.fontColor]};
  line-height: 21px;
  letter-spacing: 0px;
  -webkit-margin-before: 0px;
  -webkit-margin-after: 0px;
  -webkit-margin-start: 0px;
  -webkit-margin-end: 0px;
`
const Paragraph: ReactComponentStyled<{}> = styled.p`
  text-align: ${(props: TextProps) => align[props.align]};
  font-family: Roboto;
  font-weight: 400;
  font-size: 16px;
  color: ${(props: CustomTextProps) => fontColor[props.fontColor]};
  letter-spacing: 0px;
  line-height: 24px;
  -webkit-margin-before: 0px;
  -webkit-margin-after: 0px;
  -webkit-margin-start: 0px;
  -webkit-margin-end: 0px;
`

type TextComponentType = typeof H1 | typeof H2 | typeof H3 | typeof H4 | typeof Paragraph

const textgroup = {
  1: H1,
  2: H2,
  3: H3,
  4: H4,
  P: Paragraph
}

export const Text = (props: TextProps) => {
  const size = props.size || '3'

  const CustomText: TextComponentType = textgroup[size]
  // add this for test purpose
  CustomText.displayName = 'CustomText'

  return (
    <CustomText fontFamily={props.fontFamily} fontColor={props.fontColor} align={props.align}>
      {props.text} {props.children}
    </CustomText>
  )
}

export default Text
